import React, { useState } from "react";
import { getSalesOrdersByTag, submitXmlToAceLynk } from "../api/erpag";
import toast from "react-hot-toast";
import { getStateCode } from "./stateAbbreviations";

const AceLynkGenerator = () => {
  const [orderIds, setOrderIds] = useState("");
  const [loading, setLoading] = useState(false);
  const [xmlFiles, setXmlFiles] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedErrorFile, setSelectedErrorFile] = useState(null);

  const formatDateMMDDYYYY = (dateStr) => {
    const date = dateStr ? new Date(dateStr) : new Date();
    return date.toLocaleDateString("en-US");
  };

  const escapeXml = (str) =>
    String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");

  const convertToAceLynkXML = (erpagData) => {
    const order = erpagData.salesOrder || {};
    const items = erpagData.items || [];
    const customer = erpagData.customer || {};
    const shipping = erpagData.shippingAddress || {};
    const company = erpagData.company || {};
    const amount = erpagData.amount || {};
    const currency = erpagData.foreignCurrency?.currency || "CAD";

    const validationErrors = [];

    const invoiceNumber = (order.documentNumber || order.tag || "")
      .trim()
      .replace(/^#+/, "");

    if (!invoiceNumber) {
      validationErrors.push("Missing invoice number (documentNumber or tag)");
    }

    const invoiceDate = order.documentDate
      ? formatDateMMDDYYYY(order.documentDate)
      : null;
    if (!invoiceDate || invoiceDate === "Invalid Date") {
      validationErrors.push("Missing or invalid invoice date");
    }

    const totalAmount = Number(amount.amount || amount.amountTaxIncl || 0);
    if (totalAmount <= 0) {
      validationErrors.push("Invoice total is zero or missing");
    }
    // === 2. Consignee (US Recipient) ===
    const consigneeName = (shipping.name || customer.customer || "").trim();
    if (!consigneeName || consigneeName === "Customer") {
      validationErrors.push("Missing consignee name");
    }

    const consigneeAddress1 = (
      shipping.address1 ||
      shipping.fullAddress ||
      ""
    ).trim();
    if (!consigneeAddress1 || consigneeAddress1 === "_") {
      validationErrors.push(
        "Missing consignee address (address1 or fullAddress)"
      );
    }

    const consigneeCity = (shipping.city || "").trim();
    if (!consigneeCity || consigneeCity === "_") {
      validationErrors.push("Missing consignee city");
    }

    const consigneeRegion = (shipping.provinceState || "").trim();
    if (!consigneeRegion || consigneeRegion === "_") {
      validationErrors.push("Missing consignee state/province");
    }

    const consigneePostal = (shipping.postalZipCode || "").trim();
    if (!consigneePostal || consigneePostal === "_") {
      validationErrors.push("Missing consignee postal/ZIP code");
    }

    // === 3. Product Items ===
    const productItems = items.filter((i) => i.itemType === "Product");

    if (productItems.length === 0) {
      validationErrors.push(
        "No product line items found (only services or empty?)"
      );
    } else {
      productItems.forEach((item, idx) => {
        const prod = item.productsAndServices || {};
        const sku = (prod.sku || "").trim();
        const description = (prod.description || "").trim();
        const harmonizedTariff = (prod.harmonizedTariff || "").trim();
        const qty = Number(prod.quantity || 0);
        const uom = (prod.uom || "").trim();
        const price = Number(item.price || 0);
        const fdaCode = (item.fdaCode || "").trim();

        if (!sku) validationErrors.push(`Item ${idx + 1}: Missing SKU`);
        if (!description)
          validationErrors.push(`Item ${idx + 1}: Missing description`);
        const skuForError = sku ? ` (SKU: ${sku})` : " (SKU: Unknown)";

        if (!harmonizedTariff) {
          validationErrors.push(
            `Item ${idx + 1}${skuForError}: Missing Harmonized Tariff (HS) Code`
          );
        } else if (!/^\d{4}\.\d{2}\.\d{4}$/.test(harmonizedTariff)) {
          validationErrors.push(
            `Item ${
              idx + 1
            }${skuForError}: Invalid HS Code format (expected XXXX.XX.XXXX)`
          );
        }
        if (qty <= 0)
          validationErrors.push(
            `Item ${idx + 1}${skuForError}: Quantity must be greater than 0`
          );
        if (!uom)
          validationErrors.push(
            `Item ${idx + 1}${skuForError}: Missing Unit of Measure (UOM)`
          );
        if (price <= 0)
          validationErrors.push(
            `Item ${idx + 1}${skuForError}: Unit price must be greater than 0`
          );
        if (!fdaCode) {
          validationErrors.push(
            `Item ${idx + 1}${skuForError}: Missing FDA Product Code`
          );
        } else {
          const cleanCode = fdaCode.replace(/--/g, "");
          if (!/^\d{2}[A-Z]{3}\d{2}$/.test(cleanCode)) {
            validationErrors.push(
              `Item ${
                idx + 1
              }${skuForError}: Invalid FDA Product Code "${fdaCode}" (expected format: XXAAAXX, e.g., 28FCH99)`
            );
          }
        }
      });
    }

    // === 4. Exporter ===
    if (!company.companyName?.trim())
      validationErrors.push("Missing exporter company name");
    if (!company.address?.trim())
      validationErrors.push("Missing exporter address");

    // === Final Check ===
    if (validationErrors.length > 0) {
      return { error: true, messages: validationErrors };
    }

    // === Generate Valid XML ===
    const itemsXml = productItems
      .map((item) => {
        const prod = item.productsAndServices || {};
        const qty = prod.quantity || 1;
        const price = Number(item.price || 0).toFixed(2);
        const lineTotal = (qty * item.price || 0).toFixed(2);
        const sku = (prod.sku || "").trim() || "NO-SKU";
        const description = (prod.description || "Product Item").trim();
        const uom = (prod.uom || "EA").trim();
        const harmonizedTariff = (prod.harmonizedTariff || "").trim();
        const fdaCode = (item.fdaCode || "").trim() || "";

        return `      <item inv_number="${escapeXml(invoiceNumber)}">
        <description>${escapeXml(description)}</description>
        <hbl_association/>
        <product_number>${escapeXml(sku)}</product_number>
        <country_of_origin>CA</country_of_origin>
        <country_of_export>US</country_of_export>
        <manufacturer>
          <mid></mid>
        </manufacturer>
        <tariff>
          <number>${escapeXml(harmonizedTariff)}</number>
        </tariff>
        <quantity>
          <amount>${qty}</amount>
          <unit_of_measure>${escapeXml(uom)}</unit_of_measure>
        </quantity>
        <price>
          <unit_price>${price}</unit_price>
          <total_price>${lineTotal}</total_price>
        </price>
        <fda tariff_number="0910996000">
          <fda_government_agency_program_code>FOO</fda_government_agency_program_code>
          <fda_agency_processing_code>PRO</fda_agency_processing_code>
          <fda_product_code>${escapeXml(fdaCode)}</fda_product_code>
          <fda_item_type>Product</fda_item_type>
          <fda_product_code_qualifier></fda_product_code_qualifier>
          <fda_intended_use_code>210.000</fda_intended_use_code>
          <!-- FDA Product Description (PG10) – Dynamic from product description -->
          <fda_product_description>${escapeXml(description)}</fda_product_description>
          <fda_trade_brand_name></fda_trade_brand_name>
          <fda_source_type_code></fda_source_type_code>
          <fda_value></fda_value>
          <fda_prior_notice_number></fda_prior_notice_number>
          <fda_constituent_element>
            <fda_source_code>39</fda_source_code>
            <fda_country_code></fda_country_code>
          </fda_constituent_element>
          <fda_qty>
            <fda_packaging_qualifier>1</fda_packaging_qualifier>
            <fda_quantity></fda_quantity>
          </fda_qty>
          <fda_arrival_information>
            <port_code>0708</port_code>
            <date_of_arrival></date_of_arrival>
            <time_of_arrival></time_of_arrival>
            <Inspection_or_lab_testing_status>A</Inspection_or_lab_testing_status>
            <Inspection_or_arrival_location_code>D-2</Inspection_or_arrival_location_code>
          </fda_arrival_information>
          <fda_entity>
            <fda_shipper>DEQ Nutrivilla</fda_shipper>
            <fda_manufacturer>
              <name>${escapeXml(
                company.companyName || "NutriVilla Foods Inc."
              )}</name>
              <address>
                <address_1>${escapeXml(
                  company.address || "1560 55e Ave"
                )}</address_1>
                <address_2></address_2>
              </address>
              <city>${escapeXml(company.city || "Lachine")}</city>
              <region>${escapeXml(getStateCode(company.provinceState) || "QC")}</region>
              <country>CA</country>
              <postal_code>${escapeXml(
                company.postalZipCode || "H8T 3J5"
              )}</postal_code>
              <contact_name></contact_name>
              <phone></phone>
              <email></email>
              <identity_qualifier></identity_qualifier>
              <identity_number></identity_number>
              <fda_registration_number>16078543976</fda_registration_number>
            </fda_manufacturer>
            <fda_deliver_to/>
            <fda_importer>
              <name>LESA00003 Importer of Record</name>
              <address>
                <address_1></address_1>
                <address_2></address_2>
              </address>
              <city></city>
              <region></region>
              <country>US</country>
              <postal_code></postal_code>
              <contact_name></contact_name>
              <phone></phone>
              <email></email>
              <identity_qualifier></identity_qualifier>
              <identity_number></identity_number>
            </fda_importer>

            <!-- Ultimate Consignee – fully dynamic (copy from your existing variables) -->
            <fda_consignee>
              <name>${escapeXml(consigneeName)}</name>
              <address>
                <address_1>${escapeXml(consigneeAddress1)}</address_1>
                <address_2>${escapeXml(shipping.address2 || "")}</address_2>
              </address>
              <city>${escapeXml(consigneeCity)}</city>
              <region>${escapeXml(getStateCode(consigneeRegion))}</region>
              <country>US</country>
              <postal_code>${escapeXml(consigneePostal)}</postal_code>
              <phone>${escapeXml(shipping.phone || "")}</phone>
            </fda_consignee>
            <fda_owner>FD1 Nutrivilla</fda_owner>
            <fda_submitter>PNS Nutrivilla</fda_submitter> 
            <fda_transmitter>PNT Bay Brokerage</fda_transmitter>
            <fda_foreign_supplier_verification>
              <name></name>
              <address>
                <address_1></address_1>
                <address_2></address_2>
              </address>
              <city></city>
              <region></region>
              <country></country>
              <postal_code></postal_code>
              <contact_name></contact_name>
              <phone></phone>
              <email></email>
              <duns></duns>
            </fda_foreign_supplier_verification>
          </fda_entity>
          <fda_production_country>CA</fda_production_country>
          <fda_shipment_country>US</fda_shipment_country>
          <fda_containers>
            <container></container>
          </fda_containers>
          <fda_point_of_contact>
            <fda_individual_qualifier></fda_individual_qualifier>
            <fda_individual_name></fda_individual_name>
            <fda_individual_telephone_number></fda_individual_telephone_number>
            <fda_individual_email></fda_individual_email>
          </fda_point_of_contact>
          <fda_affirmations_of_compliance>
            <Code></Code>
            <Description></Description>
          </fda_affirmations_of_compliance>
        </fda>
      </item>`;
      })
      .join("\n");

    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<document xmlns="http://www.acelynk.com/edi/imports/shipment">
  <shipment reference="${escapeXml(invoiceNumber)}">
    <header>
      <importer_of_record>
        <account>LESA00003</account>
        <filer>BGK</filer>
      </importer_of_record>
      <exporter>
        <name>${escapeXml(company.companyName || "_")}</name>
        <address>
          <address_1>${escapeXml(company.address || "_")}</address_1>
          <address_2></address_2>
        </address>
        <city>${escapeXml(company.city || "_")}</city>
       <region>${escapeXml(getStateCode(company.provinceState) || "QC")}</region>
        <country>CA</country>
        <postal_code>${escapeXml(company.postalZipCode || "_")}</postal_code>
        <phone></phone>
        <fax></fax>
        <email></email>
        <tax_id></tax_id>
        <fda_registration_number>16078543976</fda_registration_number>
        <duns_number>20-221-5476</duns_number>
      </exporter>
      <consignee>
        <name>${escapeXml(consigneeName)}</name>
        <address>
          <address_1>${escapeXml(consigneeAddress1)}</address_1>
          <address_2>${escapeXml(shipping.address2 || "")}</address_2>
        </address>
        <city>${escapeXml(consigneeCity)}</city>
        <region>${escapeXml(getStateCode(consigneeRegion))}</region>
        <country>US</country>
        <postal_code>${escapeXml(consigneePostal)}</postal_code>
        <phone>${escapeXml(shipping.phone || "")}</phone>
        <email></email>
        <tax_id></tax_id>
        <fda_registration_number></fda_registration_number>
      </consignee>
      <buyer>
        <name></name>
        <address>
          <address_1></address_1>
          <address_2></address_2>
        </address>
        <city></city>
        <region></region>
        <country></country>
        <postal_code></postal_code>
        <phone></phone>
        <fax></fax>
        <email></email>
        <tax_id></tax_id>
        <fda_registration_number></fda_registration_number>
      </buyer>
      <general>
        <entry_filing_type>11</entry_filing_type>
        <skip_tariff_validation>false</skip_tariff_validation>
        <invoice_number>${escapeXml(invoiceNumber)}</invoice_number>
        <invoice_date>${invoiceDate}</invoice_date>
        <MOT>30</MOT>
        <BillLading>
          <SCAC></SCAC>
          <BillNo></BillNo>
          <Quantity></Quantity>
          <QuantityUOM></QuantityUOM>
          <BillType></BillType>
        </BillLading>
        <entry_port></entry_port>
        <export_date></export_date>
        <invoice_total>${totalAmount.toFixed(2)}</invoice_total>
        <origin_country>CA</origin_country>
        <export_country>US</export_country>
        <gross_weight></gross_weight>
        <manifest_description></manifest_description>
        <invoice_totals>
          <invoice_number></invoice_number>
          <total></total>
        </invoice_totals>
        <post_summary_correction>true</post_summary_correction>
        <psc_for_other_filer>true</psc_for_other_filer>
        <psc_entry_number></psc_entry_number>
        <psc_broker_ref_no></psc_broker_ref_no>
        <voyage_trip_number>DOES NOT EXIST</voyage_trip_number>
      </general>
      <ndc>
        <ndc_charges>
          <code></code>
          <value></value>
          <currency></currency>
        </ndc_charges>
        <duty></duty>
        <brokerage></brokerage>
      </ndc>
      <terms>
        <related>_</related>
        <inco>Prepaid</inco>
        <invoice_currency>${escapeXml(currency)}</invoice_currency>
        <prices_in_column_total_price_include>
          <brokerage></brokerage>
          <usduty></usduty>
        </prices_in_column_total_price_include>
      </terms>
    </header>
    <line>
${itemsXml}
    </line>
  </shipment>
</document>`;

    return { xml };
  };

  const retryGeneration = async (file) => {
    setXmlFiles((prev) =>
      prev.map((f) =>
        f.id === file.id
          ? {
              ...f,
              generating: true,
              generationError: null,
              validationMessages: [],
            }
          : f
      )
    );

    try {
      const response = await getSalesOrdersByTag(file.originalTag);
      if (response.success && response.data && response.data.length > 0) {
        const result = convertToAceLynkXML(response.data[0]);

        setXmlFiles((prev) =>
          prev.map((f) =>
            f.id === file.id
              ? {
                  ...f,
                  xml: result.error ? null : result.xml,
                  filename: `${file.id}_AceLynk.xml`,
                  generated: !result.error,
                  generating: false,
                  generationError: result.error
                    ? `Validation failed (${result.messages.length} issue${
                        result.messages.length > 1 ? "s" : ""
                      })`
                    : null,
                  validationMessages: result.error ? result.messages : [],
                  sending: false,
                  sent: false,
                  sendError: null,
                }
              : f
          )
        );
        toast.success(`Regenerated ${file.id}`);
      } else {
        throw new Error(response.error || "Not found");
      }
    } catch (err) {
      const errorMsg = err.message || "Retry failed";
      setXmlFiles((prev) =>
        prev.map((f) =>
          f.id === file.id
            ? {
                ...f,
                generationError: errorMsg,
                generating: false,
                validationMessages: [],
              }
            : f
        )
      );
      toast.error(`Retry failed for ${file.id}: ${errorMsg}`);
    }
  };

  const handleSendToAceLynk = async (file) => {
    if (!file.xml || typeof file.xml !== "string") {
      toast.error(`Cannot send ${file.id}: Validation failed or XML missing`);
      return;
    }

    setXmlFiles((prev) =>
      prev.map((f) =>
        f.id === file.id ? { ...f, sending: true, sendError: null } : f
      )
    );

    const result = await submitXmlToAceLynk(file.xml);

    setXmlFiles((prev) =>
      prev.map((f) =>
        f.id === file.id
          ? {
              ...f,
              sending: false,
              sent: result.success,
              sendError: result.success ? null : result.error,
            }
          : f
      )
    );

    if (result.success) toast.success(`Sent ${file.id}`);
    else toast.error(`Failed to send ${file.id}: ${result.error}`);
  };

  const handleBulkSend = async () => {
    const toSend = xmlFiles.filter(
      (f) =>
        selectedIds.has(f.id) && f.generated && !f.sent && !f.sending && f.xml
    );
    for (const file of toSend) {
      await handleSendToAceLynk(file);
      await new Promise((r) => setTimeout(r, 600));
    }
  };

  const handleBulkRetryGeneration = async () => {
    const failed = xmlFiles.filter(
      (f) => selectedIds.has(f.id) && !f.generated && !f.generating
    );
    for (const file of failed) {
      await retryGeneration(file);
      await new Promise((r) => setTimeout(r, 1000));
    }
  };

  const handleBulkRetrySend = async () => {
    const failedSends = xmlFiles.filter(
      (f) => selectedIds.has(f.id) && f.generated && f.sendError
    );
    for (const file of failedSends) {
      await handleSendToAceLynk(file);
      await new Promise((r) => setTimeout(r, 600));
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setXmlFiles([]);
    setSelectedIds(new Set());

    const rawIds = orderIds
      .trim()
      .split(/\s+|\n|,/g)
      .map((s) => s.trim())
      .filter(Boolean);

    if (rawIds.length === 0) {
      setLoading(false);
      return;
    }

    const results = [];

    for (let i = 0; i < rawIds.length; i++) {
      let input = rawIds[i];
      let tag = input.replace(/^SO[-_]?/i, "").replace(/^#*/, "");
      if (!tag) continue;
      tag = `#${tag}`;
      const displayId =
        input.startsWith("SO") || input.startsWith("#") ? input : `SO-${input}`;

      const fileEntry = {
        id: displayId,
        originalTag: tag,
        generated: false,
        generating: true,
        xml: null,
        filename: `${displayId}_AceLynk.xml`,
        sending: false,
        sent: false,
        generationError: null,
        sendError: null,
        validationMessages: [],
      };

      results.push(fileEntry);
      setXmlFiles([...results]);

      try {
        const response = await getSalesOrdersByTag(tag);

        if (response.success && response.data && response.data.length > 0) {
          const result = convertToAceLynkXML(response.data[0]);

          if (result.error) {
            fileEntry.generationError = `Validation failed (${
              result.messages.length
            } issue${result.messages.length > 1 ? "s" : ""})`;
            fileEntry.validationMessages = result.messages;
            fileEntry.generated = false;
            fileEntry.xml = null;
          } else {
            fileEntry.xml = result.xml;
            fileEntry.generated = true;
            fileEntry.generationError = null;
            fileEntry.validationMessages = [];
          }
        } else {
          fileEntry.generationError = response.error || "Order not found";
          fileEntry.validationMessages = [];
        }
      } catch (err) {
        if (err.response?.status === 429) {
          fileEntry.generationError = "Rate limited – retrying...";
          toast.warning(`Rate limited on ${displayId}`);
          await new Promise((r) => setTimeout(r, 5000 + i * 1000));
          i--;
          continue;
        }
        fileEntry.generationError = "Network error / timeout";
        fileEntry.validationMessages = [];
      }

      fileEntry.generating = false;
      setXmlFiles([...results]);
      await new Promise((r) => setTimeout(r, 1500));
    }

    setLoading(false);
  };

  const downloadXml = (xml, filename) => {
    const blob = new Blob([xml], { type: "text/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const viewXml = (xml) => {
    const blob = new Blob([xml], { type: "text/xml" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    const visibleGenerated = filteredFiles.filter((f) => f.generated);
    if (visibleGenerated.every((f) => selectedIds.has(f.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleGenerated.map((f) => f.id)));
    }
  };

  const filteredFiles = xmlFiles.filter((file) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "generated") return file.generated;
    if (statusFilter === "gen-failed")
      return !file.generated && !file.generating;
    if (statusFilter === "not-sent")
      return file.generated && !file.sent && !file.sending;
    if (statusFilter === "sent") return file.sent;
    if (statusFilter === "send-failed") return file.generated && file.sendError;
    return true;
  });

  const selectedCount = selectedIds.size;
  const selectedGenerated = xmlFiles.filter(
    (f) => selectedIds.has(f.id) && f.generated
  );
  const selectedGenFailed = xmlFiles.filter(
    (f) => selectedIds.has(f.id) && !f.generated && !f.generating
  );
  const selectedSendFailed = xmlFiles.filter(
    (f) => selectedIds.has(f.id) && f.sendError
  );

  const filterBtnClass = (filter) =>
    statusFilter === filter
      ? "px-4 py-2 rounded-lg font-medium bg-black text-white"
      : "px-4 py-2 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300";

  return (
    <div className="mx-auto p-8 bg-white rounded-2xl shadow-xl border border-gray-100">
      <h1 className="text-4xl font-extrabold mb-8 text-gray-900 text-center">
        AceLynk XML Generator
      </h1>

      <div className="mb-6">
        <label className="block text-gray-800 font-semibold mb-2">
          Order IDs (one per line or comma separated)
        </label>
        <textarea
          className="w-full h-48 p-5 border-2 border-gray-300 rounded-xl font-mono text-sm focus:border-black focus:outline-none resize-none transition-all shadow-sm"
          placeholder="Paste order IDs here... e.g. #12345, SO-67890"
          value={orderIds}
          onChange={(e) => setOrderIds(e.target.value)}
          disabled={loading}
        />
        <p className="text-xs text-gray-500 mt-2">
          Supports comma, space, or newline separated values.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        {!loading &&
          xmlFiles.length > 0 &&
          xmlFiles.some((f) => f.generated) && (
            <button
              onClick={() => {
                xmlFiles.forEach(
                  (file) => file.xml && downloadXml(file.xml, file.filename)
                );
              }}
              className="px-4 py-3 bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl shadow-md transition"
            >
              Download All ({xmlFiles.filter((f) => f.generated).length})
            </button>
          )}

        {loading && (
          <div className="flex-1 text-center text-gray-700 font-medium text-lg animate-pulse">
            Generating: {xmlFiles.length} /{" "}
            {
              orderIds
                .trim()
                .split(/\s+|\n|,/g)
                .filter(Boolean).length
            }
          </div>
        )}

        {!loading && xmlFiles.length === 0 && <div className="flex-1" />}

        <button
          onClick={handleGenerate}
          disabled={loading || !orderIds.trim()}
          className={`px-6 py-3 rounded-xl font-semibold shadow-md transition ${
            loading || !orderIds.trim()
              ? "bg-gray-300 cursor-not-allowed text-gray-600"
              : "bg-black text-white hover:bg-gray-900"
          }`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </span>
          ) : (
            "Generate XML"
          )}
        </button>
      </div>

      {xmlFiles.length > 0 && (
        <div className="mt-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Results</h2>

          <div className="mb-5 flex items-center gap-4 flex-wrap">
            <span className="text-gray-700 font-medium">Filter:</span>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setStatusFilter("all")}
                className={filterBtnClass("all")}
              >
                All ({xmlFiles.length})
              </button>
              <button
                onClick={() => setStatusFilter("generated")}
                className={filterBtnClass("generated")}
              >
                Generated ({xmlFiles.filter((f) => f.generated).length})
              </button>
              <button
                onClick={() => setStatusFilter("gen-failed")}
                className={filterBtnClass("gen-failed")}
              >
                Failed (
                {xmlFiles.filter((f) => !f.generated && !f.generating).length})
              </button>
              <button
                onClick={() => setStatusFilter("not-sent")}
                className={filterBtnClass("not-sent")}
              >
                Not Sent (
                {
                  xmlFiles.filter((f) => f.generated && !f.sent && !f.sending)
                    .length
                }
                )
              </button>
              <button
                onClick={() => setStatusFilter("sent")}
                className={filterBtnClass("sent")}
              >
                Sent ({xmlFiles.filter((f) => f.sent).length})
              </button>
              <button
                onClick={() => setStatusFilter("send-failed")}
                className={filterBtnClass("send-failed")}
              >
                Send Failed ({xmlFiles.filter((f) => f.sendError).length})
              </button>
            </div>
          </div>

          {selectedCount > 0 && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between flex-wrap gap-4">
              <span className="font-medium text-blue-900">
                {selectedCount} selected
              </span>
              <div className="flex gap-3 flex-wrap">
                {selectedGenerated.filter((f) => !f.sent).length > 0 && (
                  <button
                    onClick={handleBulkSend}
                    className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition"
                  >
                    Send Selected (
                    {selectedGenerated.filter((f) => !f.sent).length})
                  </button>
                )}
                {selectedGenFailed.length > 0 && (
                  <button
                    onClick={handleBulkRetryGeneration}
                    className="px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition"
                  >
                    Retry Generation ({selectedGenFailed.length})
                  </button>
                )}
                {selectedSendFailed.length > 0 && (
                  <button
                    onClick={handleBulkRetrySend}
                    className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition"
                  >
                    Retry Send ({selectedSendFailed.length})
                  </button>
                )}
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="px-4 py-2 text-blue-700 hover:bg-blue-100 rounded-lg font-medium transition"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-xl shadow border border-gray-200">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 w-12">
                    <input
                      type="checkbox"
                      checked={
                        filteredFiles.filter((f) => f.generated).length > 0 &&
                        filteredFiles
                          .filter((f) => f.generated)
                          .every((f) => selectedIds.has(f.id))
                      }
                      onChange={toggleSelectAll}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                    />
                  </th>
                  <th className="py-3 px-4 text-left text-gray-600 font-semibold">
                    Order ID
                  </th>
                  <th className="py-3 px-4 text-left text-gray-600 font-semibold">
                    Generation
                  </th>
                  <th className="py-3 px-4 text-left text-gray-600 font-semibold">
                    Errors
                  </th>
                  <th className="py-3 px-4 text-left text-gray-600 font-semibold">
                    Send Status
                  </th>
                  <th className="py-3 px-4 text-left text-gray-600 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file) => {
                  const isGenSuccess = file.generated;
                  const isGenProcessing = file.generating;
                  const hasValidationErrors =
                    file.validationMessages?.length > 0;

                  return (
                    <tr
                      key={file.id}
                      className={`border-b hover:bg-gray-50 transition h-16 ${
                        selectedIds.has(file.id) ? "bg-blue-50" : ""
                      }`}
                    >
                      <td className="py-3 px-4 text-center">
                        {isGenSuccess && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(file.id)}
                            onChange={() => toggleSelect(file.id)}
                            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                          />
                        )}
                      </td>

                      <td className="py-3 px-4">
                        <div className="font-mono font-bold text-gray-800">
                          {file.id}
                        </div>
                        {file.filename && (
                          <div className="text-xs text-gray-500">
                            {file.filename}
                          </div>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        {isGenProcessing ? (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 animate-pulse">
                            Generating...
                          </span>
                        ) : isGenSuccess ? (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            Generated
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            Failed
                          </span>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        {hasValidationErrors ? (
                          <button
                            onClick={() => setSelectedErrorFile(file)}
                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-full transition shadow-sm"
                          >
                            {file.validationMessages.length} error
                            {file.validationMessages.length > 1 ? "s" : ""}
                          </button>
                        ) : file.generationError && !hasValidationErrors ? (
                          <span className="text-xs text-red-600">
                            {file.generationError}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        {isGenSuccess ? (
                          file.sending ? (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 animate-pulse">
                              Sending...
                            </span>
                          ) : file.sent ? (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                              Sent
                            </span>
                          ) : file.sendError ? (
                            <>
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                Send Failed
                              </span>
                              <div className="text-xs text-red-600 mt-1 max-w-xs truncate">
                                {file.sendError}
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-500 text-xs">
                              Not sent
                            </span>
                          )
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        <div className="flex gap-2 flex-wrap">
                          {isGenSuccess ? (
                            <>
                              <button
                                onClick={() => viewXml(file.xml)}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium"
                              >
                                View
                              </button>
                              <button
                                onClick={() =>
                                  downloadXml(file.xml, file.filename)
                                }
                                className="px-3 py-1.5 bg-black hover:bg-gray-900 text-white rounded text-xs font-medium"
                              >
                                Download
                              </button>
                              <button
                                onClick={() => handleSendToAceLynk(file)}
                                disabled={file.sending || file.sent}
                                className={`px-3 py-1.5 rounded text-xs font-medium ${
                                  file.sent
                                    ? "bg-green-600 text-white"
                                    : file.sending
                                    ? "bg-yellow-500 text-white cursor-not-allowed"
                                    : "bg-purple-600 hover:bg-purple-700 text-white"
                                }`}
                              >
                                {file.sent
                                  ? "Sent"
                                  : file.sending
                                  ? "Sending..."
                                  : "Send"}
                              </button>
                            </>
                          ) : !file.generating ? (
                            <button
                              onClick={() => retryGeneration(file)}
                              className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded text-xs font-medium"
                            >
                              Retry
                            </button>
                          ) : null}
                          {file.sendError && (
                            <button
                              onClick={() => handleSendToAceLynk(file)}
                              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium"
                            >
                              Retry Send
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-6 text-center text-gray-700 text-sm font-medium">
            <span className="text-green-700">
              {xmlFiles.filter((f) => f.generated).length} Generated
            </span>
            {" • "}
            <span className="text-red-700">
              {xmlFiles.filter((f) => !f.generated && !f.generating).length}{" "}
              Failed
            </span>
            {" • "}
            <span className="text-green-700">
              {xmlFiles.filter((f) => f.sent).length} Sent
            </span>
            {` • Total: ${xmlFiles.length}`}
            {selectedCount > 0 && ` • Selected: ${selectedCount}`}
          </div>
        </div>
      )}

      {/* Error Details Modal */}
      {selectedErrorFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                Validation Errors – {selectedErrorFile.id}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                This order cannot be sent until these issues are fixed in ERPAG.
              </p>
            </div>
            <div className="p-6 overflow-y-auto max-h-96">
              <ul className="space-y-2">
                {selectedErrorFile.validationMessages.map((msg, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-red-600 mt-0.5">•</span>
                    <span className="text-gray-800">{msg}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setSelectedErrorFile(null)}
                className="px-6 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-900 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && xmlFiles.length === 0 && orderIds.trim() && (
        <div className="text-center text-gray-500 mt-10">
          <p className="text-lg">Ready to generate XML files!</p>
          <p className="text-sm mt-2">Click the button above to start</p>
        </div>
      )}
    </div>
  );
};

export default AceLynkGenerator;




